import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { TranslateService } from '@ngx-translate/core';
import { JobState } from '../../../../shared/models/generated/ziroModels';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { MigrationsAssessmentsContext } from '../../../services/migrations-assessments.context';
import { MigrationsService } from '../../../services/migrations.service';
import { RingCentralCallQueuesContext } from '../ring-central-call-queues.context';

interface CallQueueListRow extends DatatableRow {
  name: string;
  extension: string;
  site: string;
  routingMethod: string;
  memberCount: number;
}

@Component({
  selector: 'ziro-ring-central-call-queues-list-view',
  standalone: false,
  templateUrl: './ring-central-call-queues-list-view.component.html',
  styleUrls: ['../../../migrations.component.scss'],
})
export class RingCentralCallQueuesListViewComponent implements OnInit, OnDestroy {
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _callQueuesContext = inject(RingCentralCallQueuesContext);
  private _migrationsService = inject(MigrationsService);

  private _refreshButtonId = 'ring-central-call-queues-list-view-refresh';
  private _tenantId: string;
  private _exportId: string;
  private _exportName: string;
  private _subscriptions = new Subscription();

  isLoading = true;
  showCustomAlert = false;
  tableDataAutomation = 'ring-central-call-queues-list-view-datatable';

  columns: DatatableColumn<CallQueueListRow>[] = [
    {
      name: 'name',
      label: 'tkey;migrations.ring_central.call_queues.list.table.name.label',
    },
    {
      name: 'extension',
      label: 'tkey;migrations.ring_central.call_queues.list.table.extension.label',
    },
    {
      name: 'site',
      label: 'tkey;migrations.ring_central.call_queues.list.table.site.label',
    },
    {
      name: 'routingMethod',
      label: 'tkey;migrations.ring_central.call_queues.list.table.routing_method.label',
    },
    {
      name: 'memberCount',
      label: 'tkey;migrations.ring_central.call_queues.list.table.member_count.label',
    },
  ];

  tableRows: CallQueueListRow[] = [];

  ngOnInit(): void {
    ({ tenantId: this._tenantId, id: this._exportId } = this._route.snapshot.params);

    this._initBottomNav();

    const assessmentsSub = this._migrationsAssessmentsContext.state$.subscribe((allExports) => {
      this._exportName = allExports.ringCentralExports.find(
        (ringExport) => ringExport.exportId === this._exportId,
      ).name;
      this._initBreadcrumbs();
    });
    this._subscriptions.add(assessmentsSub);

    const callQueuesSub = this._callQueuesContext.state$.subscribe((callQueues) => {
      this.tableRows = callQueues.map((callQueue) => ({
        id: callQueue.id,
        name: callQueue.name,
        extension: callQueue.number,
        site: callQueue.site,
        routingMethod: this._translateService.instant(
          this._callQueuesContext.getRoutingMethodTkey(callQueue.routingMethod),
        ),
        memberCount: callQueue.callQueueMembership.length,
        actions: [
          {
            dataAutomation: 'ring-central-call-queue-detail-button',
            type: ButtonTypes.LINK,
            style: ButtonStyles.PRIMARY,
            icon: ZiroIcons.NEXT,
            disabled: false,
            href: `${window.location.href}/${callQueue.id}`,
          },
        ],
      }));
      this.isLoading = false;
      this.showCustomAlert = false;
    });
    this._subscriptions.add(callQueuesSub);

    const jobStateSub = this._callQueuesContext.jobState$.subscribe((status) => {
      if (status.lastRunTime) {
        this._migrationsService.setBottomNavTimestamp(
          status.lastRunTime,
          'tkey;migrations.number_report.refresh_button.last_updated_label',
        );
      }

      if (status.error && status.jobState === JobState.IDLE) {
        this.showCustomAlert = true;
        this.isLoading = false;
      }

      const isRefreshing = !status.lastRunTime || status.jobState !== JobState.IDLE;
      this._migrationsService.dispatchButtonState(this._refreshButtonId, {
        pending: isRefreshing,
        buttonDisableState: { disabled: isRefreshing },
      });
    });
    this._subscriptions.add(jobStateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );
  }

  onRefreshClick(): void {
    this.isLoading = true;
    this.showCustomAlert = false;
    const sub = this._callQueuesContext.refresh().subscribe();
    this._subscriptions.add(sub);
  }

  private _initBreadcrumbs(): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: this._exportName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/ring-central/${this._exportId}`,
      },
      { label: 'tkey;migrations.ring_central.call_queues.title' },
    ]);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: this._refreshButtonId,
          dataAutomation: 'ring-central-call-queues-list-view-refresh-button',
          label: 'tkey;migrations.number_report.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.DEFAULT,
          cb: () => this.onRefreshClick(),
        },
        {
          id: 'ring-central-call-queues-list-view-back-button',
          dataAutomation: 'ring-central-call-queues-list-view-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => {
            this._router.navigate(['..'], { relativeTo: this._route });
          },
        },
      ]),
    );
  }
}
